import { useState, useCallback } from "react";

// Local-TZ-safe ISO formatter — produces "YYYY-MM-DD" from a Date without
// converting through UTC (which off-by-ones the date for IST and other
// positive-offset zones — was the source of the "2026-03-31 → 2027-03-30"
// bug in PDF exports).
const toLocalISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Calendar-FY fallback — used only when no active company FY is available.
const calendarFY = () => {
  const now = new Date();
  const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return {
    start: toLocalISO(new Date(y, 3, 1)),
    end: toLocalISO(new Date(y + 1, 2, 31)),
  };
};

/**
 * Shared report period state with an explicit "Go" / apply gate.
 *
 * Default range = **current Indian Financial Year** (Apr 1 → Mar 31). This is
 * what the user explicitly asked for: reports should open showing "this year"
 * regardless of what the active company's `fy_start` happens to be set to.
 * The user can then adjust the range and hit Go.
 *
 * Two range states:
 *   • `range`        — what the user is currently typing/picking in the inputs
 *   • `appliedRange` — what the report is currently fetched against
 */
export function useReportRange(initial) {
  const fy = calendarFY();
  const defaultStart = initial?.start || fy.start;
  const defaultEnd = initial?.end || fy.end;
  const initialRange = { start: defaultStart, end: defaultEnd };
  const [range, setRangeState] = useState(initialRange);
  const [applied, setApplied] = useState(initialRange);
  const setRange = useCallback((next) => setRangeState(next), []);
  const apply = useCallback(() => setApplied(range), [range]);
  const params = {};
  if (applied.start) params.start = applied.start;
  if (applied.end) params.end = applied.end;
  return { range, setRange, applied, apply, params };
}

// On-screen label — keeps the Unicode arrow for browser rendering.
export const formatRangeLabel = ({ start, end }) => {
  if (!start && !end) return "All time";
  if (start && end) return `${start} → ${end}`;
  if (start) return `From ${start}`;
  return `Until ${end}`;
};

// PDF-safe formatter — ASCII only (no Unicode arrows / em-dashes), full month
// names, full year. Picks the most professional phrasing depending on whether
// the range corresponds to a financial year or a custom slice.
//   Apr 1 ↔ Mar 31 → "For the year ended 31 March 2027"
//   anything else  → "For the period 01 April 2026 to 31 March 2027"
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const _parseISO = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  return { y, m, d };
};

const _pretty = (iso) => {
  const p = _parseISO(iso);
  return p ? `${String(p.d).padStart(2, "0")} ${MONTHS[p.m - 1]} ${p.y}` : iso;
};
export const formatPrettyDate = (iso) => _pretty(iso);

export const formatRangePretty = ({ start, end }) => {
  if (!start && !end) return "All available data";
  const ps = _parseISO(start), pe = _parseISO(end);
  // Detect Indian FY: 01 April ↔ 31 March of the next year
  if (ps && pe && ps.m === 4 && ps.d === 1 && pe.m === 3 && pe.d === 31 && pe.y === ps.y + 1) {
    return `For the year ended ${_pretty(end)}`;
  }
  if (start && end) return `For the period ${_pretty(start)} to ${_pretty(end)}`;
  if (start) return `From ${_pretty(start)}`;
  return `Up to ${_pretty(end)}`;
};

// "FY 2026-27" style FY label, useful for the PDF header strip.
export const formatFYLabel = ({ start, end }) => {
  const ps = _parseISO(start), pe = _parseISO(end);
  if (ps && pe && ps.m === 4 && ps.d === 1 && pe.m === 3 && pe.d === 31 && pe.y === ps.y + 1) {
    return `FY ${ps.y}-${String(pe.y).slice(-2)}`;
  }
  return "";
};
